import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Palette } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const SETTINGS_TABS = [
  { value: "appearance", label: "Aparência" },
];

type TenantFormData = {
  name: string;
  email?: string | null;
};

const tenantSchema = z.object({
  name: z.string().min(1),
  email: z.string().email().optional().nullable(),
});

type ProfileFormData = {
  nome_completo: string;
  telefone?: string | null;
  cargo?: string | null;
  data_nascimento?: string | null;
};

const profileSchema = z.object({
  nome_completo: z.string().min(1),
  telefone: z.string().optional().nullable(),
  cargo: z.string().optional().nullable(),
  data_nascimento: z.string().optional().nullable(),
});

export default function Settings() {
  const { tenantId, tenantName, isAdminSaas } = useTenant();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [googleConnected, setGoogleConnected] = useState(false);

  useEffect(() => {
    const getUser = supabase?.auth?.getUser as any;
    if (typeof getUser !== 'function') {
      return;
    }
    getUser().then(({ data: { user } }: any) => {
      setUserId(user?.id || null);
      // Verificar se usuário tem Google vinculado
      const hasGoogle = user?.app_metadata?.providers?.includes('google') || 
                        user?.identities?.some(i => i.provider === 'google');
      setGoogleConnected(!!hasGoogle);
    });
  }, []);

  // Fetch tenant data
  const { data: tenant } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("*")
        .eq("id", tenantId)
        .single() as any;
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const tenantForm = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      name: "",
      email: "",
    },
  });

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      nome_completo: "",
      telefone: "",
      cargo: "",
      data_nascimento: "",
    },
  });

  // Update forms when data is loaded
  useEffect(() => {
    if (tenant) {
      tenantForm.reset({
        name: tenant.name || "",
        email: tenant.email || "",
      });
    }
  }, [tenant, tenantForm]);

  useEffect(() => {
    if (profile) {
      profileForm.reset({
        nome_completo: profile.nome_completo || "",
        telefone: profile.telefone || "",
        cargo: profile.cargo || "",
        data_nascimento: profile.data_nascimento || "",
      });
    }
  }, [profile, profileForm]);

  const updateTenantMutation = useMutation({
    mutationFn: async (data: TenantFormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      const { error } = await supabase
        .from("tenants" as any)
        .update({
          name: data.name,
          email: data.email || null,
        })
        .eq("id", tenantId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      toast.success("Dados da empresa atualizados!");
      if (tenantId) {
        void logCrud({ entitySlug: "entity:tenants", operationSlug: "update", entityId: tenantId, screenSlug: "configuracoes/empresa", before: (tenant as any), after: (companyForm.getValues() as any) });
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar dados: " + error.message);
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      if (!userId) throw new Error("Usuário não autenticado");
      
      const { error } = await supabase
        .from("profiles")
        .update({
          nome_completo: data.nome_completo,
          telefone: data.telefone || null,
          cargo: data.cargo || null,
          data_nascimento: data.data_nascimento || null,
        })
        .eq("id", userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      toast.success("Perfil atualizado!");
      if (userId) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "update", entityId: userId, screenSlug: "configuracoes/perfil", before: (profile as any), after: (profileForm.getValues() as any) });
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar perfil: " + error.message);
    },
  });

  const handleLinkGoogle = async () => {
    const { error } = await supabase.auth.linkIdentity({
      provider: 'google',
    });

    if (error) {
      toast.error("Erro ao vincular conta Google: " + error.message);
    } else {
      toast.success("Conta Google vinculada com sucesso!");
    }
  };

  const handleUnlinkGoogle = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const googleIdentity = user?.identities?.find(i => i.provider === 'google');
    
    if (!googleIdentity) {
      toast.error("Conta Google não encontrada");
      return;
    }

    const { error } = await supabase.auth.unlinkIdentity(googleIdentity);

    if (error) {
      toast.error("Erro ao desvincular conta Google: " + error.message);
    } else {
      setGoogleConnected(false);
      toast.success("Conta Google desvinculada com sucesso!");
    }
  };
  const [activeTab, setActiveTab] = useState<string>("appearance");

  return (
    <div className="space-y-6" data-cy="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-cy="settings-title">Configurações</h1>
        <p className="text-muted-foreground">
          Gerencie suas configurações e preferências
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-cy="settings-tabs">
        <div className="md:hidden">
          <Select value={activeTab} onValueChange={setActiveTab} data-cy="settings-tabs-select">
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma seção" />
            </SelectTrigger>
            <SelectContent>
              {SETTINGS_TABS.map((tab) => (
                <SelectItem key={tab.value} value={tab.value}>
                  {tab.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden w-full md:grid md:grid-cols-1" data-cy="settings-tabs-list">
          <TabsTrigger value="appearance" data-cy="appearance-tab">
            <Palette className="h-4 w-4 mr-2" />
            Aparência
          </TabsTrigger>
        </TabsList>

        <TabsContent value="appearance" data-cy="appearance-tab-content">
          <Card data-cy="appearance-card">
            <CardHeader>
              <CardTitle data-cy="appearance-card-title">Aparência</CardTitle>
              <CardDescription>
                Personalize a aparência do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between" data-cy="theme-section">
                <div className="space-y-0.5">
                  <Label>Tema</Label>
                  <p className="text-sm text-muted-foreground">
                    Escolha entre tema claro, escuro ou automático
                  </p>
                </div>
                <ThemeToggle data-cy="theme-toggle" />
              </div>

              <div className="pt-4 border-t" data-cy="appearance-placeholder">
                <p className="text-sm text-muted-foreground">
                  Mais opções de personalização estarão disponíveis em breve.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
